import { useEffect, useMemo, useRef, useState, type FormEvent, type ReactNode } from "react";
import { Disclaimer } from "../components/Disclaimer";
import { PageHeader } from "../components/PageHeader";
import { config } from "../lib/config";
import {
  launchCities,
  organizationTypes,
  requestPurposes,
} from "../lib/constants";

type SubmitState =
  | { status: "idle" }
  | { status: "submitting" }
  | { status: "success"; requestNo: string }
  | { status: "error"; message: string };

type TurnstileWidgetId = string;

type TurnstileApi = {
  render: (
    container: HTMLElement,
    options: {
      sitekey: string;
      theme?: "light" | "dark" | "auto";
      callback?: (token: string) => void;
      "expired-callback"?: () => void;
      "error-callback"?: () => void;
    },
  ) => TurnstileWidgetId;
  reset: (widgetId?: TurnstileWidgetId) => void;
  remove?: (widgetId: TurnstileWidgetId) => void;
};

declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

export function RequestEvaluation() {
  const turnstileContainerRef = useRef<HTMLDivElement | null>(null);
  const turnstileWidgetIdRef = useRef<TurnstileWidgetId | null>(null);
  const [files, setFiles] = useState<File[]>([]);
  const [turnstileToken, setTurnstileToken] = useState("");
  const [submitState, setSubmitState] = useState<SubmitState>({ status: "idle" });
  const fileSummary = useMemo(
    () =>
      files.map((file) => `${file.name} (${(file.size / 1024 / 1024).toFixed(2)}MB)`),
    [files],
  );

  useEffect(() => {
    if (!config.turnstileSiteKey || !turnstileContainerRef.current) {
      return;
    }

    let disposed = false;
    const renderTurnstile = () => {
      if (
        disposed ||
        !turnstileContainerRef.current ||
        !window.turnstile ||
        turnstileWidgetIdRef.current
      ) {
        return;
      }

      turnstileWidgetIdRef.current = window.turnstile.render(turnstileContainerRef.current, {
        sitekey: config.turnstileSiteKey,
        theme: "light",
        callback: (token) => setTurnstileToken(token),
        "expired-callback": () => setTurnstileToken(""),
        "error-callback": () => setTurnstileToken(""),
      });
    };

    if (window.turnstile) {
      renderTurnstile();
    } else {
      const existingScript = document.querySelector<HTMLScriptElement>("#turnstile-script");
      if (existingScript) {
        existingScript.addEventListener("load", renderTurnstile, { once: true });
      } else {
        const script = document.createElement("script");
        script.id = "turnstile-script";
        script.src = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
        script.async = true;
        script.defer = true;
        script.addEventListener("load", renderTurnstile, { once: true });
        document.head.appendChild(script);
      }
    }

    return () => {
      disposed = true;
      setTurnstileToken("");
      if (turnstileWidgetIdRef.current && window.turnstile?.remove) {
        window.turnstile.remove(turnstileWidgetIdRef.current);
      }
      turnstileWidgetIdRef.current = null;
    };
  }, []);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = event.currentTarget;
    setSubmitState({ status: "submitting" });
    const formData = new FormData(form);
    files.forEach((file) => formData.append("attachments", file));

    try {
      const response = await fetch("/api/requests", {
        method: "POST",
        body: formData,
      });
      const payload = await readRequestResponse(response);
      if (!response.ok || !payload.request_no) {
        throw new Error(payload.error ?? "تعذر إرسال الطلب.");
      }
      setSubmitState({ status: "success", requestNo: payload.request_no });
      form.reset();
      setFiles([]);
      setTurnstileToken("");
      if (turnstileWidgetIdRef.current) {
        window.turnstile?.reset(turnstileWidgetIdRef.current);
      }
    } catch (error) {
      setSubmitState({
        status: "error",
        message: error instanceof Error ? error.message : "تعذر إرسال الطلب.",
      });
      resetTurnstile();
    }
  }

  function resetTurnstile() {
    setTurnstileToken("");
    if (turnstileWidgetIdRef.current) {
      window.turnstile?.reset(turnstileWidgetIdRef.current);
    }
  }

  return (
    <>
      <PageHeader
        eyebrow="طلب خدمة"
        title="طلب تقييم أصول عقارية"
        description="أرسل بيانات الجهة والعقار والمرفقات بصيغة PDF، وستظهر الطلبات للإدارة الداخلية داخل لوحة التحكم."
      />
      <section className="bg-sand py-10">
        <div className="container">
          <Disclaimer />
        </div>
      </section>
      <section className="py-20">
        <div className="container grid gap-10 lg:grid-cols-[1.35fr_0.65fr]">
          <form
            className="grid gap-8 rounded-lg border border-line bg-white p-6 shadow-card"
            onSubmit={handleSubmit}
          >
            <div>
              <p className="eyebrow">نموذج استقبال مؤسسي</p>
              <h2 className="text-2xl font-bold text-charcoal">بيانات طلب التقييم</h2>
              <p className="mt-2 text-sm leading-7 text-ash">
                جميع الحقول المطلوبة تساعد الإدارة على تحديد النطاق ومراجعة المرفقات قبل التواصل.
              </p>
            </div>

            <FormSection title="بيانات الجهة والتواصل">
              <div className="field">
                <label htmlFor="organization_type">نوع الجهة</label>
                <select id="organization_type" name="organization_type" required>
                  <option value="">اختر</option>
                  {organizationTypes.map((item) => (
                    <option key={item}>{item}</option>
                  ))}
                </select>
              </div>
              <div className="field">
                <label htmlFor="organization_name">اسم الجهة</label>
                <input id="organization_name" name="organization_name" required />
              </div>
              <div className="field">
                <label htmlFor="contact_full_name">اسم مسؤول التواصل</label>
                <input id="contact_full_name" name="contact_full_name" required />
              </div>
              <div className="field">
                <label htmlFor="contact_title">الصفة الوظيفية</label>
                <input id="contact_title" name="contact_title" required />
              </div>
              <div className="field">
                <label htmlFor="mobile">رقم الجوال</label>
                <input id="mobile" name="mobile" type="tel" required />
              </div>
              <div className="field">
                <label htmlFor="official_email">البريد الرسمي</label>
                <input id="official_email" name="official_email" type="email" required />
              </div>
            </FormSection>

            <FormSection title="بيانات العقار">
              <div className="field">
                <label htmlFor="city">المدينة</label>
                <select id="city" name="city" required>
                  <option value="">اختر</option>
                  {launchCities.map((city) => (
                    <option key={city}>{city}</option>
                  ))}
                </select>
              </div>
              <div className="field">
                <label htmlFor="district">الحي</label>
                <input id="district" name="district" required />
              </div>
              <div className="field">
                <label htmlFor="property_type">نوع العقار</label>
                <input id="property_type" name="property_type" required />
              </div>
              <div className="field">
                <label htmlFor="location_url">رابط الموقع</label>
                <input id="location_url" name="location_url" type="url" />
              </div>
            </FormSection>

            <FormSection title="نطاق الطلب والمرفقات">
              <div className="field md:col-span-2">
                <label htmlFor="request_purpose">الغرض من الطلب</label>
                <select id="request_purpose" name="request_purpose" required>
                  <option value="">اختر</option>
                  {requestPurposes.map((purpose) => (
                    <option key={purpose}>{purpose}</option>
                  ))}
                </select>
              </div>
              <div className="field md:col-span-2">
                <label htmlFor="short_description">وصف مختصر</label>
                <textarea id="short_description" name="short_description" rows={5} required />
              </div>
              <div className="field md:col-span-2">
                <label htmlFor="attachments">المرفقات PDF فقط</label>
                <input
                  id="attachments"
                  type="file"
                  accept="application/pdf,.pdf"
                  multiple
                  onChange={(event) => {
                    setFiles(Array.from(event.currentTarget.files ?? []).slice(0, 5));
                  }}
                />
                <p className="text-sm leading-7 text-ash">
                  PDF فقط — حتى 5 ملفات — 20MB كحد أقصى لكل ملف.
                </p>
                <p className="text-sm leading-7 text-ash">
                  المرفقات تُحفظ في تخزين خاص ومشفّر، ولا تُنشر بروابط عامة. يمكن
                  لإدارة كيان الوصول إلى الملفات فقط من خلال لوحة الإدارة المحمية.
                </p>
                {fileSummary.length ? (
                  <ul className="grid gap-1 text-sm text-graphite">
                    {fileSummary.map((item) => (
                      <li key={item}>{item}</li>
                    ))}
                  </ul>
                ) : null}
              </div>
            </FormSection>

            <div className="grid gap-3 rounded-md bg-offwhite p-4">
              <label className="flex gap-3 leading-8 text-graphite">
                <input name="consent_engineering_only" type="checkbox" required />
                <span>أقر بأن الخدمة المطلوبة تقييم هندسي وليست تقييماً للقيمة السوقية للعقار.</span>
              </label>
              <label className="flex gap-3 leading-8 text-graphite">
                <input name="consent_data_use" type="checkbox" required />
                <span>أوافق على استخدام البيانات والمرفقات لغرض مراجعة الطلب وإدارته.</span>
              </label>
              <label className="flex gap-3 leading-8 text-graphite">
                <input name="consent_pdf_files" type="checkbox" required />
                <span>أقر بأن الملفات المرفوعة بصيغة PDF ومرتبطة بالطلب.</span>
              </label>
            </div>

            {config.turnstileSiteKey ? (
              <>
                <div ref={turnstileContainerRef} />
                <input name="cf-turnstile-response" type="hidden" value={turnstileToken} />
              </>
            ) : (
              <p className="rounded-md border border-line bg-sand p-4 text-sm leading-7 text-ash">
                سيتم تفعيل Cloudflare Turnstile عند إضافة مفتاح الموقع في البيئة.
              </p>
            )}

            <div className="rounded-md border border-line bg-sand p-5">
              <h3 className="text-lg font-bold text-charcoal">ملاحظات قبل إرسال الطلب</h3>
              <div className="mt-3 space-y-3 text-sm leading-8 text-graphite">
                <p>
                  لا توفر المنصة في هذه المرحلة بوابة متابعة إلكترونية للعميل. تتم
                  مراجعة الطلبات ومتابعة حالتها داخلياً لدى إدارة شركة كيان
                  الاستشارات الهندسية.
                </p>
                <p>
                  بعد إرسال الطلب سيظهر رقم طلب خاص بكم، ويرجى الاحتفاظ به
                  لاستخدامه عند التواصل.
                </p>
                <p>
                  لا يرسل النظام إشعاراً بريدياً آلياً في هذه المرحلة، وسيتم
                  التواصل معكم عند الحاجة عبر بيانات التواصل المدخلة في النموذج.
                </p>
              </div>
            </div>

            <button
              className="btn btn-primary w-full"
              type="submit"
              disabled={submitState.status === "submitting"}
            >
              {submitState.status === "submitting" ? "جاري الإرسال..." : "طلب تقييم أصول عقارية"}
            </button>

            {submitState.status === "success" ? (
              <RequestSuccessMessage requestNo={submitState.requestNo} />
            ) : null}
            {submitState.status === "error" ? (
              <div className="rounded-md border border-red-200 bg-red-50 p-4 leading-8 text-red-900">
                {submitState.message}
              </div>
            ) : null}
          </form>

          <aside className="rounded-lg bg-charcoal p-7 text-white">
            <p className="eyebrow">قبل إرسال الطلب</p>
            <ul className="grid gap-4 leading-8 text-white/78">
              <li>لا توفر المنصة في هذه المرحلة بوابة متابعة إلكترونية للعميل.</li>
              <li>تتم مراجعة الطلبات ومتابعة حالتها داخلياً لدى إدارة شركة كيان.</li>
              <li>لا يرسل النظام إشعاراً بريدياً آلياً في هذه المرحلة.</li>
              <li>المرفقات تُحفظ في تخزين خاص ومشفّر ولا تُنشر بروابط عامة.</li>
            </ul>
          </aside>
        </div>
      </section>
    </>
  );
}

export function RequestSuccessMessage({ requestNo }: { requestNo: string }) {
  return (
    <div className="space-y-3 rounded-md border border-green-200 bg-green-50 p-4 leading-8 text-green-900">
      <p>تم استلام طلبكم بنجاح.</p>
      <p>رقم الطلب: {requestNo}</p>
      <p>
        يرجى الاحتفاظ برقم الطلب للرجوع إليه عند التواصل. ستتم مراجعة الطلب
        داخلياً من قبل إدارة شركة كيان الاستشارات الهندسية، وسيتم التواصل معكم
        عند الحاجة عبر بيانات التواصل المدخلة.
      </p>
      <p>
        لا توجد بوابة متابعة للعميل في هذه المرحلة، ولا يتم إرسال إشعار بريد آلي
        تلقائي حالياً.
      </p>
    </div>
  );
}

function FormSection({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="grid gap-5 rounded-md border border-line p-5 md:grid-cols-2">
      <h3 className="text-xl font-bold text-charcoal md:col-span-2">{title}</h3>
      {children}
    </section>
  );
}

async function readRequestResponse(response: Response) {
  const contentType = response.headers.get("content-type") ?? "";
  if (contentType.includes("application/json")) {
    return (await response.json()) as {
      request_no?: string;
      error?: string;
    };
  }

  return {
    error:
      "تعذر إرسال الطلب لأن الخادم أعاد استجابة غير متوقعة. يرجى إعادة المحاولة أو التواصل مع الإدارة.",
  };
}
